import React, {useContext, useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import {RadioButtonCheckedOutlined, SendToMobileOutlined} from "@mui/icons-material";
import {useNavigate} from "react-router-dom";
import {Input, ALL_FORMATS, UrlSource, BlobSource} from "mediabunny";
import {useAssetStore} from "../../storage/AssetStore";
import {
    LibrettoProjectTypes,
    IsVideoTrackType,
    LibrettoRecordingMode
} from "../../utils/utils";
import { fitToCanvas, maybeLockCanvasToFirstVideo } from "../../utils/canvasSizing";
import {ProjectAssetsStore} from "../../state/ProjectAssetsStore";
import {CompositionService} from "../../librettoEditor/services/CompositionService";
import {CompositionStateStore} from "../../librettoEditor/store/CompositionStateStore";
import SelectableCardButton from "../NewHome/SelectableCard";
import {RecordSomethingMenu} from "../NewHome/ProjectContextMenu";
import {PAGE_MODES} from "./V2Editor";
import {uploadFilesWithPoolLimit} from "../../utils/multiFileUpload";
import {CreateScriptClipForComposition} from "./utils";
import type {ScriptLayerInputConfig} from "../../engine/clips/script/script.interfaces";
import { trackSessionEvent } from "../../analytics/sessionEvents";
import { AuthContext } from "../../context/AuthContext";
import { CombineFilesDialog } from "./components/CombineFilesDialog";
import { deriveSequenceTitleFromFiles, populateSequenceFromTracks } from "./sequenceAutoCreate";
import { probeAviVideoStream } from "../../utils/aviProbe";

// ============================================================================
// Supported File Types Configuration
// ============================================================================
const SUPPORTED_AUDIO_EXTENSIONS = ['wav', 'mp3', 'aiff', 'aif', 'm4a', 'flac', 'ogg', 'opus'];
// MOV is included here but files are probed for codec support using MediaBunny
const SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'm4v', 'mov', 'webm'];
const SUPPORTED_IMAGE_EXTENSIONS = ['bmp', 'jpeg', 'jpg', 'png', 'gif', 'webp'];

const ALL_SUPPORTED_EXTENSIONS = [
    ...SUPPORTED_AUDIO_EXTENSIONS,
    ...SUPPORTED_VIDEO_EXTENSIONS,
    ...SUPPORTED_IMAGE_EXTENSIONS,
];

// Human-readable format descriptions for the "Supported formats" panel
const SUPPORTED_FORMATS_INFO = {
    audio: {
        containers: 'WAV, MP3, AIFF, M4A, FLAC, OGG',
        codecs: 'AAC, PCM, OPUS',
    },
    video: {
        containers: 'MP4, M4V, MOV, WEBM',
        codecs: 'H.264, HEVC, VP9, VP8',
        note: 'ProRes and other professional codecs are not supported',
    },
    image: {
        formats: 'BMP, JPEG, PNG, GIF, WEBP',
    },
    serverProcessed: {
        containers: 'AVI',
        codecs: 'DIVX',
        note: 'These files are converted on the server before they can be used.',
    },
};

// Default colors for ScriptClip sources (should match transcript + waveform palettes)
const DEFAULT_SOURCE_COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316'];

/**
 * Check if a file extension is supported
 */
function isExtensionSupported(filename: string): boolean {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    return ALL_SUPPORTED_EXTENSIONS.includes(ext);
}

/**
 * Get the file extension from a filename
 */
function getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toUpperCase() || 'Unknown';
}

/**
 * Categorize a file extension
 */
function getFileCategory(filename: string): 'audio' | 'video' | 'image' | 'unknown' {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    if (SUPPORTED_AUDIO_EXTENSIONS.includes(ext)) return 'audio';
    if (SUPPORTED_VIDEO_EXTENSIONS.includes(ext)) return 'video';
    if (SUPPORTED_IMAGE_EXTENSIONS.includes(ext)) return 'image';
    return 'unknown';
}

// Known unsupported codec identifiers (from container metadata)
// These are typically ProRes variants and other professional codecs
const UNSUPPORTED_CODEC_IDS = [
    'apch', // ProRes 422 HQ
    'apcn', // ProRes 422
    'apcs', // ProRes 422 LT
    'apco', // ProRes 422 Proxy
    'ap4h', // ProRes 4444
    'ap4x', // ProRes 4444 XQ
    'icod', // Apple Intermediate Codec
    'qtrle', // QuickTime Animation (RLE)
    'rpza', // Apple Video
    'smc',  // Apple Graphics
    'rle',  // Animation codec
];

/**
 * Probe a video file to check if its codec is supported for web playback.
 * Uses MediaBunny to detect the actual codec inside the container.
 * Returns { supported: true } or { supported: false, codec: string, reason: string }
 */
async function probeVideoCodecSupport(file: File): Promise<{
    supported: boolean;
    codec?: string;
    reason?: string;
}> {
    try {
        const source = new BlobSource(file);
        const input = new Input({ source, formats: ALL_FORMATS });
        
        // Get video track
        const videoTrack = await input.getPrimaryVideoTrack();
        if (!videoTrack) {
            // No video track - could be audio-only in video container, allow it
            return { supported: true };
        }
        
        // Check the internal codec ID (raw identifier from container)
        const internalId = videoTrack.internalCodecId;
        const internalIdStr = typeof internalId === 'string' ? internalId.toLowerCase() : '';
        
        // Check against known unsupported codecs
        for (const unsupportedId of UNSUPPORTED_CODEC_IDS) {
            if (internalIdStr === unsupportedId || internalIdStr.startsWith(unsupportedId)) {
                // Map internal ID to human-readable codec name
                let codecName = 'Unknown';
                if (internalIdStr.startsWith('apc') || internalIdStr.startsWith('ap4')) {
                    codecName = 'ProRes';
                } else if (internalIdStr === 'qtrle' || internalIdStr === 'rle') {
                    codecName = 'Animation (RLE)';
                } else if (internalIdStr === 'icod') {
                    codecName = 'Apple Intermediate';
                }
                return {
                    supported: false,
                    codec: codecName,
                    reason: `${codecName} codec is not supported for web playback`,
                };
            }
        }
        
        // Also check if WebCodecs can decode this track
        const canDecode = await videoTrack.canDecode();
        if (!canDecode) {
            // Get codec info for better error message
            const codec = videoTrack.codec || internalIdStr || 'Unknown';
            return {
                supported: false,
                codec: codec.toUpperCase(),
                reason: `${codec.toUpperCase()} codec cannot be decoded in the browser`,
            };
        }
        
        return { supported: true };
    } catch (e) {
        // If probing fails, we can't determine support - allow the file and let later stages handle it
        console.warn('[probeVideoCodecSupport] Failed to probe file:', file.name, e);
        return { supported: true };
    }
}

async function probeAviDivxForServerProcessing(file: File): Promise<{
    eligible: boolean;
    codec?: string;
    reason?: string;
}> {
    try {
        const normalizeFourCC = (val?: string) => String(val ?? "").replace(/\0/g, "").trim().toUpperCase();

        const probe = await probeAviVideoStream(file);
        if (!probe.isAvi) {
            return { eligible: false, reason: probe.reason || "Not an AVI file" };
        }

        const handler = normalizeFourCC(probe.videoStream?.handler);
        const compression = normalizeFourCC(probe.videoStream?.compression);
        const candidates = [handler, compression].filter((c) => Boolean(c));

        if (candidates.includes("DIVX")) {
            return { eligible: true, codec: "DIVX" };
        }

        if (candidates.length === 0) {
            return { eligible: false, reason: "Could not read AVI video codec" };
        }

        return { eligible: false, reason: "AVI codec is not eligible for server processing" };
    } catch (e) {
        console.warn('[probeAviDivxForServerProcessing] Failed to probe file:', file.name, e);
        return { eligible: false, reason: 'Could not read AVI codec' };
    }
}

/**
 * Validate a file for upload support.
 * Checks extension first, then probes MOV files specifically for codec support
 * (since MOV can contain ProRes and other unsupported professional codecs).
 */
async function validateFileForUpload(file: File): Promise<{
    mode: 'local_ok' | 'server_process_ok' | 'rejected';
    extension: string;
    codec?: string;
    reason?: string;
}> {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const extension = ext.toUpperCase();
    
    // Local-first supported extensions
    if (isExtensionSupported(file.name)) {
        // Only MOV files need codec probing (can contain ProRes, QTRLE, etc.)
        // Other containers (MP4, WebM) typically only have web-compatible codecs
        if (ext === 'mov') {
            const codecResult = await probeVideoCodecSupport(file);
            if (!codecResult.supported) {
                return {
                    mode: 'rejected',
                    extension,
                    codec: codecResult.codec,
                    reason: codecResult.reason,
                };
            }
        }
        return { mode: 'local_ok', extension };
    }

    // Server-processed allowlist (narrow for now):
    // - AVI container with DIVX (MPEG-4 Part 2) video codec.
    if (ext === 'avi') {
        const probe = await probeAviDivxForServerProcessing(file);
        if (probe.eligible) {
            return {
                mode: 'server_process_ok',
                extension,
                codec: probe.codec,
            };
        }
        return {
            mode: 'rejected',
            extension,
            reason: probe.reason || `${extension} file format is not supported`,
        };
    }

    return {
        mode: 'rejected',
        extension,
        reason: `${extension} file format is not supported`,
    };
}

// ============================================================================
// Unsupported Files Dialog
// ============================================================================
interface UnsupportedFilesDialogProps {
    isOpen: boolean;
    onClose: () => void;
    files: Array<{ name: string; extension: string }>;
    processableFiles?: Array<{ name: string; extension: string }>;
    onUploadProcessable?: () => Promise<void> | void;
}

const UnsupportedFilesDialog: React.FC<UnsupportedFilesDialogProps> = ({
    isOpen,
    onClose,
    files,
    processableFiles,
    onUploadProcessable,
}) => {
    const [showSupportedFormats, setShowSupportedFormats] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    const rejectedCount = files?.length ?? 0;
    const processableCount = processableFiles?.length ?? 0;

    const subtitle = (() => {
        if (processableCount > 0 && rejectedCount > 0) {
            return 'Some files need server processing. Others aren’t supported.';
        }
        if (processableCount > 1) { // Plural
            return 'These files can be imported, but they need server processing before you can use them.';
        }
        if (processableCount > 0) { // Singular
            return 'This file can be imported, but it needs server processing before you can use it.';
        }
        return `The following ${rejectedCount === 1 ? 'file is' : 'files are'} not supported and ${rejectedCount === 1 ? 'was' : 'were'} not uploaded.`;
    })();

    const handleUploadProcessable = async () => {
        if (!onUploadProcessable) return;
        if (isUploading) return;
        setIsUploading(true);
        try {
            await onUploadProcessable();
        } catch (e) {
            console.warn('[UnsupportedFilesDialog] Failed to upload server-processed files', e);
        } finally {
            setIsUploading(false);
        }
    };

    const textForPlural = "We’ll upload and convert these files on the server. They’ll be usable once they’re ready."
    const textForSingular = "We’ll upload and convert this file on the server. It’ll be usable once it’s ready."

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: '420px',
                    padding: '20px 24px',
                    borderRadius: '12px',
                    boxShadow: '0px 20px 60px rgba(15, 23, 42, 0.08)',
                }
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography sx={{ 
                    fontFamily: 'Inter',
                    fontSize: '15px',
                    fontWeight: 500,
                    lineHeight: '22px',
                    color: '#1A1A1A',
	                }}>
	                    Review files
	                </Typography>
                <IconButton 
                    size="small" 
                    onClick={onClose}
                    sx={{ 
                        color: '#9CA3AF',
                        padding: '4px',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                    }}
                >
                    <Box component="span" sx={{ fontSize: '18px', lineHeight: 1 }}>×</Box>
                </IconButton>
            </Box>
            
            {/* Subtitle */}
            <Typography sx={{ 
                fontFamily: 'Inter',
                fontSize: '13px',
                fontWeight: 400,
                color: '#6B7280',
                mb: 2,
                lineHeight: '18px',
	            }}>
	                {subtitle}
	            </Typography>

	            {processableCount > 0 && (
	                <Box sx={{ mb: rejectedCount > 0 ? 2 : 1.5 }}>
	                    <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
	                        Needs processing
	                    </Typography>
	                    <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px', mb: 1.25 }}>
                            {processableCount > 1 ? textForPlural : textForSingular}
	                    </Typography>
	                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '180px', overflowY: 'auto' }}>
	                        {processableFiles?.map((file, index) => (
	                            <Box
	                                key={`processable-${index}`}
	                                sx={{
	                                    display: 'flex',
	                                    alignItems: 'center',
	                                    gap: 1.5,
	                                    padding: '8px 12px',
	                                    backgroundColor: 'rgba(43, 107, 253, 0.06)',
	                                    borderRadius: '8px',
	                                    border: '1px solid rgba(43, 107, 253, 0.18)',
	                                }}
	                            >
	                                <CloudUploadOutlinedIcon sx={{ fontSize: 18, color: '#2B6BFD', flexShrink: 0 }} />
	                                <Box sx={{ flex: 1, minWidth: 0 }}>
	                                    <Typography
	                                        sx={{
	                                            fontFamily: 'Inter',
	                                            fontSize: '13px',
	                                            fontWeight: 500,
	                                            color: '#1A1A1A',
	                                            overflow: 'hidden',
	                                            textOverflow: 'ellipsis',
	                                            whiteSpace: 'nowrap',
	                                        }}
	                                    >
	                                        {file.name}
	                                    </Typography>
	                                </Box>
	                                <Typography
	                                    sx={{
	                                        fontFamily: 'Inter',
	                                        fontSize: '12px',
	                                        fontWeight: 500,
	                                        color: '#6B7280',
	                                        backgroundColor: '#F3F4F6',
	                                        padding: '2px 8px',
	                                        borderRadius: '4px',
	                                        flexShrink: 0,
	                                    }}
	                                >
	                                    {file.extension}
	                                </Typography>
	                            </Box>
	                        ))}
	                    </Box>
	                </Box>
	            )}

	            {rejectedCount > 0 && (
	                <Box sx={{ mb: 2 }}>
	                    {processableCount > 0 && (
	                        <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.75 }}>
	                            Not supported
	                        </Typography>
	                    )}
	                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: '180px', overflowY: 'auto' }}>
	                        {files.map((file, index) => (
	                            <Box
	                                key={`rejected-${index}`}
	                                sx={{
	                                    display: 'flex',
	                                    alignItems: 'center',
	                                    gap: 1.5,
	                                    padding: '8px 12px',
	                                    backgroundColor: '#FEF2F2',
	                                    borderRadius: '8px',
	                                    border: '1px solid #FECACA',
	                                }}
	                            >
	                                <ErrorOutlineIcon sx={{ fontSize: 18, color: '#EF4444', flexShrink: 0 }} />
	                                <Box sx={{ flex: 1, minWidth: 0 }}>
	                                    <Typography
	                                        sx={{
	                                            fontFamily: 'Inter',
	                                            fontSize: '13px',
	                                            fontWeight: 500,
	                                            color: '#1A1A1A',
	                                            overflow: 'hidden',
	                                            textOverflow: 'ellipsis',
	                                            whiteSpace: 'nowrap',
	                                        }}
	                                    >
	                                        {file.name}
	                                    </Typography>
	                                </Box>
	                                <Typography
	                                    sx={{
	                                        fontFamily: 'Inter',
	                                        fontSize: '12px',
	                                        fontWeight: 500,
	                                        color: '#6B7280',
	                                        backgroundColor: '#F3F4F6',
	                                        padding: '2px 8px',
	                                        borderRadius: '4px',
	                                        flexShrink: 0,
	                                    }}
	                                >
	                                    {file.extension}
	                                </Typography>
	                            </Box>
	                        ))}
	                    </Box>
	                </Box>
	            )}

            {/* Expandable Supported Formats Section */}
            <Box sx={{ borderTop: '1px solid #E5E7EB', pt: 1.5 }}>
                <Box
                    onClick={() => setShowSupportedFormats(!showSupportedFormats)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        cursor: 'pointer',
                        padding: '6px 0',
                        '&:hover': { opacity: 0.8 },
                    }}
                >
                    <Typography sx={{
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        fontWeight: 500,
                        color: '#2B6BFD',
                    }}>
                        View supported formats
                    </Typography>
                    <ExpandMoreIcon 
                        sx={{ 
                            fontSize: 18, 
                            color: '#2B6BFD',
                            transform: showSupportedFormats ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.2s ease',
                        }} 
                    />
                </Box>
                
                {showSupportedFormats && (
                    <Box sx={{ 
                        mt: 1.5,
                        padding: '12px',
                        backgroundColor: '#F9FAFB',
                        borderRadius: '8px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                    }}>
                        {/* Audio */}
                        <Box>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                                Audio
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                                {SUPPORTED_FORMATS_INFO.audio.containers}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#9CA3AF', lineHeight: '14px' }}>
                                Codecs: {SUPPORTED_FORMATS_INFO.audio.codecs}
                            </Typography>
                        </Box>
                        
                        {/* Video */}
                        <Box>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                                Video
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                                {SUPPORTED_FORMATS_INFO.video.containers}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#9CA3AF', lineHeight: '14px' }}>
                                Codecs: {SUPPORTED_FORMATS_INFO.video.codecs}
                            </Typography>
                        </Box>
                        
                        {/* Image */}
                        <Box>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                                Image
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                                {SUPPORTED_FORMATS_INFO.image.formats}
                            </Typography>
                        </Box>

                        {/* Server-processed */}
                        <Box>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', fontWeight: 600, color: '#1A1A1A', mb: 0.5 }}>
                                Server-processed
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                                {SUPPORTED_FORMATS_INFO.serverProcessed.containers}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#9CA3AF', lineHeight: '14px' }}>
                                Codecs: {SUPPORTED_FORMATS_INFO.serverProcessed.codecs}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Inter', fontSize: '11px', color: '#9CA3AF', lineHeight: '14px' }}>
                                {SUPPORTED_FORMATS_INFO.serverProcessed.note}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                {processableCount > 0 ? (
                    <>
                        <Button
                            variant="outlined"
                            onClick={onClose}
                            disabled={isUploading}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '10px',
                                fontFamily: 'Inter',
                                fontSize: '13px',
                                fontWeight: 500,
                                color: '#111111',
                                borderColor: '#E5E7EB',
                                '&:hover': {
                                    borderColor: '#D1D5DB',
                                    backgroundColor: 'rgba(17, 24, 39, 0.04)',
                                },
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            disableElevation
                            onClick={handleUploadProcessable}
                            disabled={isUploading}
                            sx={{
                                textTransform: 'none',
                                borderRadius: '10px',
                                fontFamily: 'Inter',
                                fontSize: '13px',
                                fontWeight: 500,
                                backgroundColor: '#111111',
                                '&:hover': { backgroundColor: '#000000' },
                            }}
                        >
                            {isUploading
                                ? 'Uploading…'
                                : `Upload & process${processableCount > 1 ? ` ${processableCount}` : ''}`}
                        </Button>
                    </>
                ) : (
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={onClose}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '10px',
                            fontFamily: 'Inter',
                            fontSize: '13px',
                            fontWeight: 500,
                            backgroundColor: '#111111',
                            '&:hover': { backgroundColor: '#000000' },
                        }}
                    >
                        Got it
                    </Button>
                )}
            </Box>
        </Dialog>
    );
};

type Props = {
    projectId: string;
    compositionId?: string;
    setPageMode: (val: string) => void;
    setRecordingMode: (num: number) => {};
    setIsScreenRecordingMode: (val: boolean) => {};
    openProjectSidebar?: () => void;
};

const CreateCompositionView: React.FC<Props> = ({projectId, compositionId, setPageMode, setRecordingMode, setIsScreenRecordingMode, openProjectSidebar}) => {
    const {UploadAsset, UploadNonLocalFirstAsset, GetTrackUrl, CreateSequence, RenameComposition} = useAssetStore();
    const authContext = useContext(AuthContext);
    const navigate = useNavigate();
    const [title, setTitle] = useState<string>("");
    const viewRootRef = useRef<HTMLDivElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [pendingAssetIds, setPendingAssetIds] = useState<string[]>([]);
    const recordButtonRef = useRef<HTMLDivElement | null>(null);
    const mobileUploadButtonRef = useRef<HTMLDivElement | null>(null);
    const [recordMenuOpen, setRecordMenuOpen] = useState<boolean>(false);
    const [recordMenuPos, setRecordMenuPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

    // State for combine files dialog (shown when multiple files are selected)
    const [combineDialogOpen, setCombineDialogOpen] = useState<boolean>(false);
    const [pendingFiles, setPendingFiles] = useState<Array<{ file: File }>>([]);
    
    // Track uploaded asset IDs for multi-file uploads (to know when to create sequence/clips)
    const [multiUploadAssets, setMultiUploadAssets] = useState<Array<{ assetId: string; fileName: string; uploadIndex: number }>>([]);
    const [multiUploadMode, setMultiUploadMode] = useState<'separate' | 'sequence' | null>(null);
    const [sequenceIdForMultiUpload, setSequenceIdForMultiUpload] = useState<string | null>(null);
    const [sequenceTitleForMultiUpload, setSequenceTitleForMultiUpload] = useState<string | null>(null);
    const multiUploadProcessingRef = useRef(false);
    const [expectedFileCount, setExpectedFileCount] = useState<number>(0);
    const [multiUploadStatus, setMultiUploadStatus] = useState<string | null>(null);
    
    // State for unsupported files dialog
    const [unsupportedFilesDialogOpen, setUnsupportedFilesDialogOpen] = useState<boolean>(false);
    const [unsupportedFiles, setUnsupportedFiles] = useState<Array<{ name: string; extension: string }>>([]);
    const [processableFiles, setProcessableFiles] = useState<Array<{ name: string; extension: string }>>([]);
    const processableRawFilesRef = useRef<File[]>([]);
    const [openReviewAfterCombine, setOpenReviewAfterCombine] = useState<boolean>(false);

    useEffect(() => {
        if (combineDialogOpen) return;
        if (!openReviewAfterCombine) return;
        setOpenReviewAfterCombine(false);
        if (unsupportedFiles.length === 0 && processableFiles.length === 0) return;
        setUnsupportedFilesDialogOpen(true);
    }, [combineDialogOpen, openReviewAfterCombine, unsupportedFiles.length, processableFiles.length]);

    // Hydrate composition title when selected composition is empty view
    useEffect(() => {
        if (!compositionId) return;
        const comps = ProjectAssetsStore.projectCompositions || [];
        const comp = comps.find((c: any) => c.id === compositionId);
        if (comp) {
            const current = comp.title || comp.name || "";
            setTitle(current);
        }
    }, [compositionId, ProjectAssetsStore.projectCompositions]);

    const handleRecordCameraClick = () => {
        trackSessionEvent("solo_video_studio_opened");
        setPageMode(PAGE_MODES.RECORDING_STUDIO);
        setRecordingMode(LibrettoRecordingMode.Video);
    }

    const handleRecordMicClick = () => {
        trackSessionEvent("solo_audio_studio_opened");
        setPageMode(PAGE_MODES.RECORDING_STUDIO);
        setRecordingMode(LibrettoRecordingMode.Audio);
    }

    const handleRecordScreenClick = () => {
        trackSessionEvent("solo_screen_studio_opened");
        setPageMode(PAGE_MODES.RECORDING_STUDIO);
        setRecordingMode(LibrettoRecordingMode.Video);
        setIsScreenRecordingMode(true);
    }

    const setResponsiveRecordMenuPosition = (anchorRect: DOMRect, preferredLeftViewport: number) => {
        const rootRect = viewRootRef.current?.getBoundingClientRect();
        if (!rootRect) return;

        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const horizontalPadding = 8;
        const verticalPadding = 8;
        const menuWidth = Math.min(260, Math.max(200, viewportWidth - horizontalPadding * 2));
        const estimatedMenuHeight = Math.min(240, Math.max(180, viewportHeight - verticalPadding * 2));

        const clampedLeft = Math.min(
            Math.max(preferredLeftViewport, horizontalPadding),
            viewportWidth - menuWidth - horizontalPadding,
        );

        let topInViewport = anchorRect.bottom + 8;
        const bottomLimit = viewportHeight - verticalPadding;
        if (topInViewport + estimatedMenuHeight > bottomLimit) {
            topInViewport = Math.max(verticalPadding, anchorRect.top - estimatedMenuHeight - 8);
        }

        const rootLeft = rootRect.left;
        const rootTop = rootRect.top;
        const rootWidth = rootRect.width;
        const maxLocalLeft = Math.max(horizontalPadding, rootWidth - menuWidth - horizontalPadding);
        const localLeft = Math.min(
            Math.max(clampedLeft - rootLeft, horizontalPadding),
            maxLocalLeft,
        );
        const localTop = Math.max(verticalPadding, topInViewport - rootTop);

        setRecordMenuPos({
            top: localTop,
            left: localLeft,
        });
    };

    // When an uploaded asset appears as a track in the store, auto-create a script clip and add to composition
    useEffect(() => {
        if (!pendingAssetIds.length) return;
        const tracks = ProjectAssetsStore.projectTracks || [];
        const nextAssetId = pendingAssetIds.find((assetId) => tracks.some((t: any) => t.assetId === assetId));
        if (!nextAssetId) return;
        const tr = tracks.find((t: any) => t.assetId === nextAssetId);
        if (!tr) return;
        void (async () => {
            try {
                const trackId = tr.trackId;
                const assetTitle = tr.title || '';
                const isVideo = IsVideoTrackType(tr.trackType);
                const url = await GetTrackUrl({trackId});
                if (!url) return;

                // Parse video dimensions from resolution string like "1920x1080"
                let trackWidth = 0;
                let trackHeight = 0;
                if (isVideo) {
                    const m = (tr.resolution || "").match(/(\d+)x(\d+)/);
                    if (m) {
                        trackWidth = parseInt(m[1], 10);
                        trackHeight = parseInt(m[2], 10);
                    }
                }

	                if (!CompositionService.isInitialized()) {
	                    setPendingAssetIds((prev) => prev.filter((id) => id !== nextAssetId));
	                    return;
	                }

	                const comp = CompositionService.composition;
	                if (isVideo && trackWidth > 0 && trackHeight > 0) {
	                    maybeLockCanvasToFirstVideo(comp, trackWidth, trackHeight);
	                }
	                const compWidth = comp?.width ?? 1920;
	                const compHeight = comp?.height ?? 1080;

                // Create media input
                const source = new UrlSource(url);
                const input = new Input({source, formats: ALL_FORMATS});

                // Build the script layer input config
                const config: ScriptLayerInputConfig = {
                    input,
                    assetId: trackId,  // For waveform lookup
                    name: '',  // Empty - will show "New Speaker" placeholder
                    color: '#3B82F6',  // Default blue color
                    gain: 1.0,
                };

	                // Add visual properties if this is a video
	                if (isVideo) {
	                    const fitted = fitToCanvas(trackWidth, trackHeight, compWidth, compHeight);
	                    config.visual = {
	                        x: compWidth / 2,
	                        y: compHeight / 2,
	                        width: fitted.width,
	                        height: fitted.height,
	                        alpha: 1.0,
	                    };
	                }

                // Create script clip using shared utility
                const scriptLayerClip = await CreateScriptClipForComposition({
                    inputConfigs: [config],
                    composition: comp,
                    stateStore: CompositionStateStore,
                    clipName: assetTitle,
                });

                if (scriptLayerClip) {
                    // Set additional metadata
                    if (isVideo) {
                        CompositionStateStore.setOriginalMediaResolutionForClip(scriptLayerClip.id, trackWidth, trackHeight);
                    }
                    
                    // Add to script layer
                    await CompositionService.addToScriptLayer(scriptLayerClip);
                    console.log('[CreateCompositionView] Added script layer clip:', scriptLayerClip.id);
                }
                
                setPendingAssetIds((prev) => prev.filter((id) => id !== nextAssetId));
            } catch (e) {
                console.error("Failed to auto-create script clip from uploaded asset:", e);
                setPendingAssetIds((prev) => prev.filter((id) => id !== nextAssetId));
            }
        })();
    }, [pendingAssetIds, ProjectAssetsStore.projectTracks]);

    // Upload a single file and return its asset ID
    const uploadSingleFile = async (file: File): Promise<string | null> => {
                try {
                    const assetId = await UploadAsset({
                        projectId,
                        inputRef: fileInputRef.current,
                        file,
                        projectType: LibrettoProjectTypes.ProjectTypeEditor,
                        filetype: file.type.startsWith('image/') ? "ImageUpload" : (file.type.startsWith('video/') ? "video" : "audio"),
                        languageCode: "auto",
                    });
            return assetId;
                } catch (e) {
                    console.error("Upload failed:", e);
            return null;
        }
    };

    const handleFilesSelected = async (files: File[]) => {
        if (!files || files.length === 0) return;

        // Give immediate UI feedback by opening the Files sidebar so the user can see upload progress.
        openProjectSidebar?.();
        
        // Validate all files (async - probes video codecs)
        const validationResults = await Promise.all(
            files.map(async (file) => ({
                file,
                validation: await validateFileForUpload(file),
            }))
        );
        
        const localOkFiles: File[] = [];
        const rejected: Array<{ name: string; extension: string }> = [];
        const processableUi: Array<{ name: string; extension: string }> = [];
        const processableRaw: File[] = [];
        
        for (const { file, validation } of validationResults) {
            const displayType = validation.codec
                ? `${validation.extension} (${validation.codec})`
                : validation.extension;

            if (validation.mode === 'local_ok') {
                localOkFiles.push(file);
            } else if (validation.mode === 'server_process_ok') {
                processableRaw.push(file);
                processableUi.push({ name: file.name, extension: displayType });
            } else {
                rejected.push({ name: file.name, extension: displayType });
            }
        }
        
        const shouldShowReview = processableUi.length > 0 || rejected.length > 0;
        if (shouldShowReview) {
            setUnsupportedFiles(rejected);
            setProcessableFiles(processableUi);
            processableRawFilesRef.current = processableRaw;
        }

        if (localOkFiles.length === 0) {
            if (shouldShowReview) setUnsupportedFilesDialogOpen(true);
            return;
        }

        const hasServerProcessingInSelection = processableRaw.length > 0;
        const willOpenCombine = !hasServerProcessingInSelection && localOkFiles.length > 1;
        if (shouldShowReview) {
            if (willOpenCombine) setOpenReviewAfterCombine(true);
            else setUnsupportedFilesDialogOpen(true);
        }
        
        // Single local-first file: upload and add to pendingAssetIds (existing flow)
        if (localOkFiles.length === 1) {
            const assetId = await uploadSingleFile(localOkFiles[0]);
            if (assetId) {
                setPendingAssetIds((prev) => [...prev, assetId]);
            }
            return;
        }
        
        // Keep it simple if server-side processing is involved in this selection:
        // don't offer Sequence combining even if multiple local-first files were selected.
        if (!willOpenCombine) {
            for (const file of localOkFiles) {
                const assetId = await uploadSingleFile(file);
                if (assetId) {
                    setPendingAssetIds((prev) => [...prev, assetId]);
                }
            }
            return;
        }

        // Multiple local-first files: start ALL uploads immediately (parallel, no limit)
        // Show dialog while uploads are in progress.
        setPendingFiles(localOkFiles.map((file) => ({ file })));
        setExpectedFileCount(localOkFiles.length);
        setCombineDialogOpen(true);
        
        // Start all uploads in parallel (no concurrency limit)
        const uploadPromises = localOkFiles.map(async (file, uploadIndex) => {
            const assetId = await uploadSingleFile(file);
            if (!assetId) return null;
            return { assetId, fileName: file.name, uploadIndex };
        });
        const results = await Promise.all(uploadPromises);
        const validUploads = results.filter(
            (r): r is { assetId: string; fileName: string; uploadIndex: number } => r !== null
        );
        
        // Store the uploaded asset IDs - they'll be processed based on user's choice
        setMultiUploadAssets(validUploads);
        console.log('[CreateCompositionView] Multi-file upload complete:', validUploads.length, 'files');
    };

    // Handler: User chose to keep files separate
    const handleKeepSeparate = () => {
        setCombineDialogOpen(false);
        setPendingFiles([]);
        setMultiUploadMode('separate');
        setMultiUploadStatus('Preparing…');
        // The multiUploadAssetIds will be processed by the useEffect below
    };

    // Handler: User chose to combine files into a sequence
    const handleCombineIntoSequence = async () => {
        setCombineDialogOpen(false);
        const files = pendingFiles.map((p) => p.file);
        setPendingFiles([]);
        
        // Create a sequence for these files
        const sequenceTitle = deriveSequenceTitleFromFiles(files);
        setSequenceTitleForMultiUpload(sequenceTitle);
        const sequenceId = await CreateSequence({ projectId, title: sequenceTitle });
        console.log('[CreateCompositionView] Created sequence for multi-upload:', sequenceId);
        
        setSequenceIdForMultiUpload(sequenceId);
        setMultiUploadMode('sequence');
        setMultiUploadStatus('Preparing…');
        // The multiUploadAssetIds will be processed by the useEffect below
    };

    // Process multi-upload assets when mode is selected and all tracks are available
    useEffect(() => {
        if (!multiUploadMode || multiUploadAssets.length === 0) return;
        if (multiUploadProcessingRef.current) return;
        
        const tracks = ProjectAssetsStore.projectTracks || [];

        const stripExt = (name: string) => name.replace(/\.[^/.]+$/, '');
        const hasDigits = (name: string) => /\d/.test(stripExt(name));
        const shouldOrderByFilename = multiUploadAssets.length > 1 && multiUploadAssets.every((a) => hasDigits(a.fileName));
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
        const orderedAssets = [...multiUploadAssets].sort((a, b) => {
            if (!shouldOrderByFilename) return a.uploadIndex - b.uploadIndex;
            return collator.compare(a.fileName, b.fileName);
        });
        const orderedAssetIds = orderedAssets.map((a) => a.assetId);
        
        // Check if all uploaded assets have appeared as tracks
        const availableTracks = orderedAssetIds
            .map((assetId) => tracks.find((t: any) => t.assetId === assetId))
            .filter((t): t is any => t !== undefined);
        
        // Wait until all tracks are available
        if (availableTracks.length < orderedAssetIds.length) {
            setMultiUploadStatus('Preparing…');
            return;
        }
        
        console.log('[CreateCompositionView] All tracks available for multi-upload:', availableTracks.length);

        const getTrackDurationSec = (tr: any): number | null => {
            const raw = tr?.duration ?? tr?.Duration ?? tr?.durationSec ?? tr?.durationSecs ?? tr?.media?.durationSec;
            const val = typeof raw === 'number' ? raw : Number(raw);
            if (!Number.isFinite(val) || val <= 0) return null;
            return val;
        };
        
        if (multiUploadMode === 'separate') {
            // "Keep separate" in V2: create ONE script clip with sources arranged sequentially.
            // Wait until durations are available so offsets are stable.
            const durations = availableTracks.map(getTrackDurationSec);
            if (durations.some((d) => d == null)) {
                setMultiUploadStatus('Preparing…');
                return;
            }

            setMultiUploadStatus('Creating…');
            multiUploadProcessingRef.current = true;

            void (async () => {
                try {
                    if (!CompositionService.isInitialized()) {
                        console.warn('[CreateCompositionView] Composition not initialized for keep-separate creation');
                        return;
                    }

                    const comp = CompositionService.composition;

	                    // First VIDEO sets the composition canvas resolution (and thus max export resolution).
	                    const firstVideoTrack = availableTracks.find((t: any) => IsVideoTrackType(t.trackType));
	                    if (firstVideoTrack) {
	                        const m = (firstVideoTrack.resolution || "").match(/(\d+)x(\d+)/);
	                        if (m) {
	                            maybeLockCanvasToFirstVideo(comp, parseInt(m[1], 10), parseInt(m[2], 10));
	                        }
	                    }

                    const compWidth = comp?.width ?? 1920;
                    const compHeight = comp?.height ?? 1080;

                    const inputConfigs: ScriptLayerInputConfig[] = [];
                    let offsetSec = 0;

                    for (let i = 0; i < availableTracks.length; i++) {
                        const tr = availableTracks[i];
                        const durationSec = durations[i] ?? 0;
                        const trackId = tr.trackId;
                        const isVideo = IsVideoTrackType(tr.trackType);
                        const url = await GetTrackUrl({ trackId });
                        if (!url) continue;

                        // Parse video dimensions
                        let trackWidth = 0;
                        let trackHeight = 0;
                        if (isVideo) {
                            const m = (tr.resolution || "").match(/(\d+)x(\d+)/);
                            if (m) {
                                trackWidth = parseInt(m[1], 10);
                                trackHeight = parseInt(m[2], 10);
                            }
                        }

                        const source = new UrlSource(url);
                        const input = new Input({ source, formats: ALL_FORMATS });

                        const config: ScriptLayerInputConfig = {
                            input,
                            assetId: trackId,
                            name: tr.title || '',
                            color: DEFAULT_SOURCE_COLORS[i % DEFAULT_SOURCE_COLORS.length],
                            gain: 1.0,
                            offsetSec,
                        };

                        if (isVideo) {
                            const fitted = fitToCanvas(trackWidth, trackHeight, compWidth, compHeight);
                            config.visual = {
                                x: compWidth / 2,
                                y: compHeight / 2,
                                width: fitted.width,
                                height: fitted.height,
                                alpha: 1.0,
                            };
                        }

                        inputConfigs.push(config);
                        offsetSec += durationSec;
                    }

                    if (inputConfigs.length === 0) {
                        console.warn('[CreateCompositionView] No valid inputs for keep-separate');
                        return;
                    }

                    const scriptLayerClip = await CreateScriptClipForComposition({
                        inputConfigs,
                        composition: comp,
                        stateStore: CompositionStateStore,
                        clipName: title || 'Recording',
                    });

                    if (scriptLayerClip) {
                        await CompositionService.addToScriptLayer(scriptLayerClip);
                        console.log('[CreateCompositionView] Added sequential script clip:', scriptLayerClip.id);

                        // Add scene boundaries at each join between parts (start of each subsequent source)
                        for (let i = 1; i < inputConfigs.length; i++) {
                            const t = Number(inputConfigs[i]?.offsetSec ?? 0);
                            if (Number.isFinite(t) && t > 0) {
                                CompositionService.splitScriptLayer(t, true);
                            }
                        }
                    }
                } catch (e) {
                    console.error('[CreateCompositionView] Failed to create keep-separate script clip:', e);
                } finally {
                    multiUploadProcessingRef.current = false;
                    setMultiUploadAssets([]);
                    setMultiUploadMode(null);
                    setSequenceIdForMultiUpload(null);
                    setSequenceTitleForMultiUpload(null);
                    setExpectedFileCount(0);
                    setMultiUploadStatus(null);
                }
            })();
        } else if (multiUploadMode === 'sequence' && sequenceIdForMultiUpload) {
            // Create a script clip from all the tracks combined
            setMultiUploadStatus('Creating…');
            multiUploadProcessingRef.current = true;
            void (async () => {
                try {
                    if (!CompositionService.isInitialized()) {
                        console.warn('[CreateCompositionView] Composition not initialized for sequence creation');
                        return;
                    }
                    
	                    const comp = CompositionService.composition;

                    // Persist the auto-created sequence so it can be edited/reused later.
                    try {
                        const userId = authContext.getUserId?.();
                        const workspaceId =
                            authContext.getCurrentWorkspaceId?.() ||
                            authContext.getDefaultWorkspaceId?.() ||
                            userId ||
                            null;
                        if (userId) {
                            const layerNames = orderedAssets.map((a) => String(a.fileName ?? "").replace(/\.[^/.]+$/, ""));
                            await populateSequenceFromTracks({
                                sequenceId: sequenceIdForMultiUpload,
                                projectId,
                                userId,
                                workspaceId,
                                tracks: availableTracks,
                                layerNames,
                            });
                        } else {
                            console.warn('[CreateCompositionView] Missing userId; skipping sequence persistence');
                        }
                    } catch (e) {
                        console.warn('[CreateCompositionView] Failed to persist sequence structure for multi-upload', e);
                    }

	                    // First VIDEO sets the composition canvas resolution (and thus max export resolution).
	                    const firstVideoTrack = availableTracks.find((t: any) => IsVideoTrackType(t.trackType));
	                    if (firstVideoTrack) {
	                        const m = (firstVideoTrack.resolution || "").match(/(\d+)x(\d+)/);
	                        if (m) {
	                            maybeLockCanvasToFirstVideo(comp, parseInt(m[1], 10), parseInt(m[2], 10));
	                        }
	                    }

	                    const compWidth = comp?.width ?? 1920;
	                    const compHeight = comp?.height ?? 1080;
                    
                    // Build input configs for all tracks
                    const inputConfigs: ScriptLayerInputConfig[] = [];
                    
                    let sourceIndex = 0;
                    for (const tr of availableTracks) {
                        const trackId = tr.trackId;
                        const isVideo = IsVideoTrackType(tr.trackType);
                        const url = await GetTrackUrl({ trackId });
                        if (!url) continue;
                        
                        // Parse video dimensions
                        let trackWidth = 0;
                        let trackHeight = 0;
                        if (isVideo) {
                            const m = (tr.resolution || "").match(/(\d+)x(\d+)/);
                            if (m) {
                                trackWidth = parseInt(m[1], 10);
                                trackHeight = parseInt(m[2], 10);
                            }
                        }
                        
                        // Create media input
                        const source = new UrlSource(url);
                        const input = new Input({ source, formats: ALL_FORMATS });
                        
                        // Build config for this source
                        const config: ScriptLayerInputConfig = {
                            input,
                            assetId: trackId,
                            name: tr.title || '',
                            color: DEFAULT_SOURCE_COLORS[sourceIndex % DEFAULT_SOURCE_COLORS.length],
                            gain: 1.0,
                        };
                        sourceIndex++;
                        
	                        // Add visual properties if video
	                        if (isVideo) {
	                            const fitted = fitToCanvas(trackWidth, trackHeight, compWidth, compHeight);
	                            config.visual = {
	                                x: compWidth / 2,
	                                y: compHeight / 2,
	                                width: fitted.width,
	                                height: fitted.height,
	                                alpha: 1.0,
	                            };
	                        }
                        
                        inputConfigs.push(config);
                    }
                    
                    if (inputConfigs.length === 0) {
                        console.warn('[CreateCompositionView] No valid inputs for sequence');
                        return;
                    }
                    
	                    // Create a single script clip with all inputs (multi-track sequence)
	                    const scriptLayerClip = await CreateScriptClipForComposition({
	                        inputConfigs,
	                        composition: comp,
                        stateStore: CompositionStateStore,
                        clipName: sequenceTitleForMultiUpload || title || 'Multi-track Recording',
                    });
                    
                    if (scriptLayerClip) {
                        await CompositionService.addToScriptLayer(scriptLayerClip);
                        console.log('[CreateCompositionView] Added multi-track script clip:', scriptLayerClip.id);
                    }
                } catch (e) {
                    console.error('[CreateCompositionView] Failed to create sequence script clip:', e);
                } finally {
                    multiUploadProcessingRef.current = false;
                    // Clear multi-upload state
                    setMultiUploadAssets([]);
                    setMultiUploadMode(null);
                    setSequenceIdForMultiUpload(null);
                    setSequenceTitleForMultiUpload(null);
                    setExpectedFileCount(0);
                    setMultiUploadStatus(null);
                }
            })();
        }
    }, [
        multiUploadMode,
        multiUploadAssets,
        sequenceIdForMultiUpload,
        sequenceTitleForMultiUpload,
        ProjectAssetsStore.projectTracks,
        GetTrackUrl,
        title,
        projectId,
        authContext.authState?.userId,
    ]);

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files ?? []);
        void handleFilesSelected(files);
    };

    const onBrowse = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        void handleFilesSelected(files);
        // reset so picking the same file(s) again triggers onChange
        e.target.value = "";
    };

    // Styles inspired by AgenticUploadContentCard, but inline
    const container = {
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '16px',
        width: '100%',
        maxWidth: { xs: '100%', sm: '600px', md: '620px' },
        padding: { xs: '0px', sm: '8px 12px' },
    };

    const titleRow = {
        alignSelf: 'stretch',
        display: 'flex',
        justifyContent: 'flex-start',
        paddingLeft: { xs: '2px', sm: '6px' },
    };

    const dropArea = {
        alignSelf: 'stretch',
        display: 'grid',
        placeContent: 'center',
        borderRadius: '10px',
        border: '1px dashed #BFBFBF',
        height: '360px',
        background: '#FFFFFF',
        cursor: 'pointer',
    };

    const helperText = {
        color: '#666',
        fontSize: '13px',
        textAlign: 'center' as const,
        marginTop: '8px',
        paddingLeft: '8px',
        paddingRight: '8px',
    };

    const uploadAreaStyle = {
        display: 'flex',
        width: '100%',
        maxWidth: '100%',
        minHeight: { xs: "220px", sm: "250px", md: "280px" },
        flexDirection: 'column',
        padding: { xs: "16px", sm: "20px", md: "24px" },
        justifyContent: 'center',
        alignItems: 'center',
        gap: "12px",
        alignSelf: 'stretch',
        borderRadius: "6px",
        border: "1px dashed #BFBFBF",
        cursor: "pointer",
        // Hover styles
        ':hover': {
            backgroundColor: "#F5F5F5",
            borderColor: "#999999",
            boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)"
        }
    }

    return (
        <Box ref={viewRootRef} sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            flexDirection: "column",
            alignItems: 'center',
            paddingTop: { xs: "24px", sm: "32px", md: "50px" },
            paddingX: { xs: "12px", sm: "20px", md: "24px" },
            justifyContent: 'flex-start',
            position: 'relative',
            overflow: 'visible',
        }}>
            <Box sx={container}>
                <Box sx={titleRow}>
                    <TextField
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Name your creation"
                        variant="standard"
                        onBlur={async () => {
                            const newName = title.trim();
                            if (compositionId && newName.length > 0) {
                                const comps = ProjectAssetsStore.projectCompositions || [];
                                const comp = comps.find((c: any) => c.id === compositionId);
                                const current = comp ? (comp.title || comp.name || "") : "";
                                if (newName !== current) {
                                    try { await RenameComposition({ compositionId, newName }); } catch {}
                                }
                            }
                        }}
                        onKeyDown={async (e) => {
                            if (e.key === 'Enter') {
                                const newName = title.trim();
                                if (compositionId && newName.length > 0) {
                                    const comps = ProjectAssetsStore.projectCompositions || [];
                                    const comp = comps.find((c: any) => c.id === compositionId);
                                    const current = comp ? (comp.title || comp.name || "") : "";
                                    if (newName !== current) {
                                        try { await RenameComposition({ compositionId, newName }); } catch {}
                                    }
                                }
                            }
                        }}
                        InputProps={{disableUnderline: true, autoComplete: "off", autoFocus: false, autoCapitalize: "off",  style: {fontSize: 26}}}
                        sx={{width: { xs: '100%', sm: '420px' }, maxWidth: '100%', marginBottom: "20px"}}
                    />
                </Box>
                <Box
                    sx={uploadAreaStyle}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <Box sx={{textAlign: 'center'}}>
                        <div style={{fontFamily: 'Inter', fontSize: 16, fontWeight: 600, color: '#333'}}>Upload file
                        </div>
                        <div style={helperText as any}>Click to browse or drag & drop supported files here</div>
                    </Box>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="video/*,audio/*,image/*"
                        multiple
                        style={{display: 'none'}}
                        onChange={onBrowse}
                    />
                </Box>
                {multiUploadStatus && (
                    <Typography sx={{ fontFamily: 'Inter', fontSize: 13, color: '#6B7280', mt: 1.5 }}>
                        {multiUploadStatus}
                    </Typography>
                )}
            </Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'stretch',
                justifyContent: 'center',
                gap: { xs: '10px', sm: '20px' },
                width: '100%',
                maxWidth: { xs: '100%', sm: '600px', md: '620px' },
                mt: { xs: 0.5, sm: 1 },
            }}>
                <Box
                    ref={recordButtonRef}
                    sx={{
                        flex: { xs: "1 1 auto", sm: "0 0 300px" },
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '300px' },
                    }}
                >
                    <SelectableCardButton
                        label={"Record"}
                        icon={<RadioButtonCheckedOutlined sx={{ color: "red" }} />}
                        onClick={() => {
                            if (recordMenuOpen) {
                                setRecordMenuOpen(false);
                                return;
                            }
                            const rect = recordButtonRef.current?.getBoundingClientRect();
                            if (rect) {
                                setResponsiveRecordMenuPosition(rect, rect.left);
                            }
                            setRecordMenuOpen(true);
                        }}
                        selected={false}
                        disabled={false}
                        large={true}
                        centered={true}
                        ref={null}
                    />
                </Box>

                <Box
                    ref={mobileUploadButtonRef}
                    sx={{
                        flex: { xs: "1 1 auto", sm: "0 0 300px" },
                        width: '100%',
                        maxWidth: { xs: '100%', sm: '300px' },
                    }}
                >
                    <SelectableCardButton
                        label={"Upload from mobile"}
                        icon={<SendToMobileOutlined fontSize={"small"} />}
                        onClick={() => {
                            if (recordMenuOpen) {
                                setRecordMenuOpen(false);
                                return;
                            }
                            const rect = mobileUploadButtonRef.current?.getBoundingClientRect();
                            if (rect) {
                                setResponsiveRecordMenuPosition(rect, rect.left);
                            }
                            setRecordMenuOpen(true);
                        }}
                        selected={false}
                        disabled={true}
                        large={true}
                        centered={true}
                        ref={null}
                    />
                </Box>
            </Box>

            {recordMenuOpen && (
                <RecordSomethingMenu
                    setOpen={setRecordMenuOpen}
                    handleRecordScreenClick={() => handleRecordScreenClick()}
                    handleRecordCameraClick={() => handleRecordCameraClick()}
                    handleRecordAudioOnlyClick={() => handleRecordMicClick()}
                    handleRecordWithOthersClick={() => {
                        trackSessionEvent("entered_studio_lobby");
                        const params = new URLSearchParams();
                        params.set("studioOrigin", "editor");
                        if (compositionId) {
                            params.set("originCompositionId", compositionId);
                        }
                        navigate(`/studio/${projectId}?${params.toString()}`);
                    }}
                    top={recordMenuPos.top}
                    left={recordMenuPos.left}
                />
            )}

            {/* Combine Files Dialog - shown when multiple files are selected */}
            <CombineFilesDialog
                isOpen={combineDialogOpen}
                onClose={() => {
                    setCombineDialogOpen(false);
                    setPendingFiles([]);
                }}
                files={pendingFiles}
                onKeepSeparate={handleKeepSeparate}
                onCombineIntoSequence={handleCombineIntoSequence}
            />
            
            {/* Unsupported Files Dialog - shown when unsupported files are detected */}
            <UnsupportedFilesDialog
                isOpen={unsupportedFilesDialogOpen}
                onClose={() => {
                    setUnsupportedFilesDialogOpen(false);
                    setUnsupportedFiles([]);
                    setProcessableFiles([]);
                    processableRawFilesRef.current = [];
                }}
                files={unsupportedFiles}
                processableFiles={processableFiles}
                onUploadProcessable={async () => {
                    const toUpload = [...(processableRawFilesRef.current ?? [])];
                    setUnsupportedFilesDialogOpen(false);
                    setUnsupportedFiles([]);
                    setProcessableFiles([]);
                    processableRawFilesRef.current = [];

                    // Give immediate UI feedback by opening the Files sidebar so the user can see upload progress.
                    openProjectSidebar?.();

                    for (const file of toUpload) {
                        await UploadNonLocalFirstAsset({
                            projectId,
                            projectType: LibrettoProjectTypes.ProjectTypeEditor,
                            file,
                            filetype: "video",
                            languageCode: "auto",
                        });
                    }
                }}
            />
        </Box>
    );
};

export default CreateCompositionView;
